import SugiyamaLayout from "app/components/flow/designer/autolayout/autolayout.js";
import utils from "core/utils";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Minimal node interface for layout processing
 */
interface FlowNode {
    id: string;
    type?: string;
}

/**
 * Minimal edge interface for layout processing
 */
interface FlowEdge {
    source: string;
    target: string;
}

/**
 * Layout node with computed depth and edge relationships
 */
interface LayoutNode {
    edges_in: string[];
    edges_out: string[];
    maxDepth: number;
    id: string;
}

/**
 * Layout result with positioning information
 */
interface LayoutResultNode extends LayoutNode {
    layer: number;
    coords: [number, number];
    order: number;
}

/**
 * Layout result mapping node IDs to layout nodes
 */
type LayoutResult = Record<string, LayoutNode>;

/**
 * Final layout result with positioning
 */
type PositionedLayoutResult = Record<string, LayoutResultNode>;

// ============================================================================
// LAYOUT CONVERSION UTILITIES
// ============================================================================

/**
 * Converts nodes and edges arrays into the layout format with edges_in, edges_out, and maxDepth
 * @param nodes - Array of node objects with id property
 * @param edges - Array of edge objects with source and target properties
 * @returns Layout object with node IDs as keys and edges_in, edges_out, maxDepth, id as values
 */

const convertToLayoutFormat = (nodes: FlowNode[], edges: FlowEdge[]): LayoutResult => {
    // Initialize result object with all nodes
    const result: LayoutResult = {};

    // Create a map of node IDs for quick lookup
    const nodeMap: Record<string, boolean> = {};
    nodes.forEach(node => {
        const nodeId = node.id;
        nodeMap[nodeId] = true;
        result[nodeId] = {
            edges_in: [],
            edges_out: [],
            maxDepth: 0,
            id: nodeId
        };
    });

    // Sort edges to ensure consistent ordering that matches the old implementation
    // The old implementation processed nodes in graphModel.models order, then their targets
    // We need to sort by the order of nodes in the nodes array, not alphabetically
    const nodeOrderMap = new Map<string, number>();
    nodes.forEach((node, index) => {
        nodeOrderMap.set(node.id, index);
    });

    const sortedEdges = [...edges].sort((a, b) => {
        const sourceOrderA = nodeOrderMap.get(a.source) ?? Number.MAX_SAFE_INTEGER;
        const sourceOrderB = nodeOrderMap.get(b.source) ?? Number.MAX_SAFE_INTEGER;

        if (sourceOrderA !== sourceOrderB) {
            return sourceOrderA - sourceOrderB;
        }

        // If same source, sort by target order in nodes array
        const targetOrderA = nodeOrderMap.get(a.target) ?? Number.MAX_SAFE_INTEGER;
        const targetOrderB = nodeOrderMap.get(b.target) ?? Number.MAX_SAFE_INTEGER;
        return targetOrderA - targetOrderB;
    });

    // Process edges to build edges_in and edges_out arrays
    sortedEdges.forEach(edge => {
        const sourceId = edge.source;
        const targetId = edge.target;

        // Add to edges_out for source node
        if (result[sourceId]) {
            result[sourceId].edges_out.push(targetId);
        }

        // Add to edges_in for target node
        if (result[targetId]) {
            result[targetId].edges_in.push(sourceId);
        }
    });

    // Calculate maxDepth using the same algorithm as graphInitializer.js
    const assignMaxNodeDepth = (nodes: LayoutResult): void => {
        // Find leaf nodes (nodes with no outgoing edges)
        const leafs = Object.values(nodes).filter(node => node.edges_out.length === 0);

        const setMaxDepthForAllParents = (n: LayoutNode): void => {
            n.edges_in.forEach((parentId: string) => {
                const parentComponent = nodes[parentId];
                if (parentComponent && (parentComponent.maxDepth || 0) <= n.maxDepth) {
                    parentComponent.maxDepth = n.maxDepth + 1;
                    setMaxDepthForAllParents(parentComponent);
                }
            });
        };

        // Start with leaf nodes at depth 0 and propagate upward
        leafs.forEach(n => {
            n.maxDepth = 0;
            setMaxDepthForAllParents(n);
        });
    };

    assignMaxNodeDepth(result);

    return result;
};

// Reorder noStreamNodes to match the ordering from the original nodes array
// This mimics the old implementation where _.each(nodes, ...) preserved the insertion order
// from graphModel.models.forEach() which built the nodes object
const getOrderedNodes = (noStreamNodes: Record<string, LayoutNode>, nodes: FlowNode[]) => {
    const orderedNoStreamNodes: Record<string, LayoutNode> = {};
    // Iterate through nodes in the same order as the original nodes array
    // to match the old implementation's ordering logic
    nodes.forEach(node => {
        if (noStreamNodes[node.id]) {
            orderedNoStreamNodes[node.id] = noStreamNodes[node.id];
        }
    });

    // Add any remaining nodes that weren't in the original nodes array
    // (this handles edge cases where nodes might be dynamically created)
    Object.keys(noStreamNodes).forEach(key => {
        if (!orderedNoStreamNodes[key]) {
            orderedNoStreamNodes[key] = noStreamNodes[key];
        }
    });

    return orderedNoStreamNodes;
};

export const replaceStreamNodes = () => {};

export const getSugiyamaLayout = (nodes: FlowNode[], edges: FlowEdge[]): PositionedLayoutResult => {
    // Separate STREAM nodes from regular nodes (same logic as graphInitializer.js)
    const streamNodes: Record<string, LayoutNode> = {};
    const noStreamNodes: Record<string, LayoutNode> = {};

    // Convert all nodes to layout format first
    const allNodesLayout = convertToLayoutFormat(nodes, edges);

    // Get node type information for STREAM detection
    const nodeTypeMap = nodes.reduce((map, node) => {
        map[node.id] = node.type;
        return map;
    }, {} as Record<string, string>);

    // Process edges to detach STREAM nodes from the graph structure
    Object.values(allNodesLayout).forEach(node => {
        // Modify edges_in to bypass STREAM nodes (STREAM inputs become STREAM's output's inputs)
        node.edges_in = node.edges_in
            .map(edgeNodeId => {
                // Check if this is a STREAM node with inputs (otherwise it's a 'detached' stream)
                const isStreamNode =
                    nodeTypeMap[edgeNodeId] === utils.ENTITIES.STREAM || edgeNodeId.includes(".STREAM");
                const isStreamWithInputs =
                    isStreamNode && allNodesLayout[edgeNodeId] && allNodesLayout[edgeNodeId].edges_in.length > 0;

                return isStreamWithInputs ? allNodesLayout[edgeNodeId].edges_in : [edgeNodeId];
            })
            .flat();

        // Modify edges_out to bypass STREAM nodes
        node.edges_out = node.edges_out
            .map(edgeNodeId => {
                const isStreamNode =
                    nodeTypeMap[edgeNodeId] === utils.ENTITIES.STREAM || edgeNodeId.includes(".STREAM");
                return isStreamNode && allNodesLayout[edgeNodeId] ? allNodesLayout[edgeNodeId].edges_out : [edgeNodeId];
            })
            .flat();

        // Separate STREAM nodes from regular nodes
        const isStreamNode = nodeTypeMap[node.id] === utils.ENTITIES.STREAM || node.id.includes(".STREAM");
        const isStreamWithInputs = isStreamNode && node.edges_in.length > 0;
        if (isStreamWithInputs) {
            streamNodes[node.id] = node;
        } else {
            noStreamNodes[node.id] = node;
        }
    });

    // const orderedNoStreamNodes = getOrderedNodes(noStreamNodes, nodes);
    // Run autolayout only on non-STREAM nodes
    const autolayoutNodes = SugiyamaLayout.autolayout(noStreamNodes) as PositionedLayoutResult;

    // Position STREAM nodes based on their parent nodes (same logic as graphInitializer.js)
    Object.values(streamNodes).forEach(streamNode => {
        // Find the parent node with maximum order (rightmost parent)
        const parentNodes = streamNode.edges_in
            .map(parentId => autolayoutNodes[parentId])
            .filter(parent => parent !== undefined);

        if (parentNodes.length > 0) {
            const parentNode = parentNodes.reduce((maxParent, current) =>
                current.order > maxParent.order ? current : maxParent
            );

            // Position STREAM node at the same coordinates as its parent
            autolayoutNodes[streamNode.id] = {
                id: streamNode.id,
                coords: [...parentNode.coords] as [number, number], // Copy coordinates
                layer: parentNode.layer,
                order: parentNode.order,
                edges_in: streamNode.edges_in,
                edges_out: streamNode.edges_out,
                maxDepth: streamNode.maxDepth
            };
        }
    });

    // Handle multiple STREAM nodes at the same position (same logic as graphInitializer.js)
    const streamNodesAtSamePosition = Object.values(autolayoutNodes)
        .filter(node => {
            const isStreamNode = nodeTypeMap[node.id] === utils.ENTITIES.STREAM || node.id.includes(".STREAM");
            return isStreamNode;
        })
        .reduce((groups, node) => {
            // Group by coordinates (using same formula as original: x + 1000 * y)
            const key = node.coords[0] + 1000 * node.coords[1];
            if (!groups[key]) groups[key] = [];
            groups[key].push(node);
            return groups;
        }, {} as Record<number, LayoutResultNode[]>);

    // Offset STREAM nodes horizontally if they overlap
    Object.values(streamNodesAtSamePosition)
        .filter(group => group.length > 1)
        .forEach(group => {
            group.forEach((streamNode, index) => {
                streamNode.coords[0] += index;
            });
        });

    return autolayoutNodes;
};

/**
 * Enhanced version that includes visual positioning with STREAM node offset
 * This function applies the same visual offset logic as the original graphInitializer.js
 */
export const getSugiyamaLayoutWithVisualOffset = (
    nodes: FlowNode[],
    edges: FlowEdge[],
    nodeWidth: number = 240,
    nodeHeight: number = 80,
    nodeSeparation: number = 32,
    rankSeparation: number = 80,
    streamVerticalOffset: number = 70
): Record<
    string,
    { id: string; layer: number; coords: [number, number]; order: number; pos: { left: number; top: number } }
> => {
    const layoutResult = getSugiyamaLayout(nodes, edges);
    const nodeTypeMap = nodes.reduce((map, node) => {
        map[node.id] = node.type;
        return map;
    }, {} as Record<string, string>);

    // Convert layout coordinates to pixel positions
    const result: Record<
        string,
        { id: string; layer: number; coords: [number, number]; order: number; pos: { left: number; top: number } }
    > = {};

    Object.values(layoutResult).forEach(node => {
        const isStreamNode = nodeTypeMap[node.id] === utils.ENTITIES.STREAM || node.id.includes(".STREAM");

        // Calculate pixel positions based on autolayout coords
        const pos = {
            left: (nodeWidth + nodeSeparation) * (node.coords ? node.coords[0] : 0),
            top: (nodeHeight + rankSeparation) * (node.coords ? node.coords[1] : 0)
        };

        // Add visual offset for STREAM nodes (same as original logic)
        if (isStreamNode) {
            pos.top += streamVerticalOffset;
        }

        result[node.id] = {
            id: node.id,
            layer: node.layer,
            coords: node.coords,
            order: node.order,
            pos: pos
        };
    });

    return result;
};
